import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import FormField from '@/components/atoms/FormField';
import type { SiteTimelineScope } from '@/domain/fishGroups';

import type { BoardCycle } from '../../types';

import styles from './CycleSelector.module.scss';

const NEW_STOCKING = 'new';
const UNASSIGNED = 'unassigned';

interface CycleSelectorProps {
    /** Planned and active cycles, then completed ones, which are listed under their own heading. */
    cycles: BoardCycle[];
    /** Groups at the site in no cycle; offered first, as a scope, while there are any. */
    unassignedCount: number;
    selected: SiteTimelineScope | null;
    onSelect: (scope: SiteTimelineScope) => void;
    /** The last option opens the New stocking modal; the selection doesn't change. */
    onNewStocking: () => void;
    /** When set, the New stocking option is disabled and explains why. */
    newStockingDisabledReason?: string | null;
    /** Id of a visible note explaining a disabled option. */
    describedBy?: string;
}

export default function CycleSelector(props: CycleSelectorProps) {
    const { cycles, unassignedCount, selected, onSelect, onNewStocking } = props;
    const { newStockingDisabledReason = null, describedBy } = props;
    const { t } = useTranslation('fishGroups');
    const id = useId();
    const open = cycles.filter((cycle) => cycle.status !== 'completed');
    const completed = cycles.filter((cycle) => cycle.status === 'completed');

    return (
        <FormField
            label={t('board.cycleSelector.label')}
            htmlFor={id}
            layout="inline"
            className={styles.selector}>
            <select
                id={id}
                className={styles.select}
                aria-describedby={describedBy}
                value={selected === null ? '' : String(selected)}
                onChange={(event) => {
                    const { value } = event.target;
                    if (value === NEW_STOCKING) {
                        if (!newStockingDisabledReason) onNewStocking();
                    } else if (value === UNASSIGNED) onSelect(UNASSIGNED);
                    else onSelect(Number(value));
                }}>
                {selected === null && <option value="">{t('board.cycleSelector.none')}</option>}
                {unassignedCount > 0 && (
                    <option value={UNASSIGNED}>
                        {t('board.cycleSelector.unassigned', { count: unassignedCount })}
                    </option>
                )}
                {open.map((cycle) => (
                    <option key={cycle.id} value={String(cycle.id)}>
                        {t('board.cycleSelector.option', {
                            name: cycle.displayName,
                            status: t(`board.cycleStatus.${cycle.status}`),
                        })}
                    </option>
                ))}
                {completed.length > 0 && (
                    <optgroup label={t('board.cycleStatus.completed')}>
                        {completed.map((cycle) => (
                            <option key={cycle.id} value={String(cycle.id)}>
                                {cycle.displayName}
                            </option>
                        ))}
                    </optgroup>
                )}
                <option
                    value={NEW_STOCKING}
                    disabled={newStockingDisabledReason !== null}
                    title={newStockingDisabledReason ?? undefined}>
                    {newStockingDisabledReason
                        ? t('board.cycleSelector.newStockingUnavailable', {
                              reason: newStockingDisabledReason,
                          })
                        : t('board.cycleSelector.newStocking')}
                </option>
            </select>
        </FormField>
    );
}
